import IntranetLayout from "@/components/IntranetLayout";
import { useState } from "react";
import { Users, Ship, Calendar, BookOpen, AlertTriangle, CheckCircle, Clock, Plus, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

/* ── Data ── */
type CrewStatus = "onboard" | "on_leave" | "mobilising" | "standby" | "unavailable";

interface CrewMember {
  id: string; name: string; role: string; nationality: string; vessel: string; status: CrewStatus;
  rotationStart: string; rotationEnd: string; certStatus: "green" | "amber" | "red";
  certIssue?: string; returnDate?: string;
}

const CREW: CrewMember[] = [
  { id: "1", name: "Capt. John Smith", role: "Master", nationality: "British", vessel: "VOFF Crewliner 01", status: "onboard", rotationStart: "2026-03-15", rotationEnd: "2026-04-26", certStatus: "green" },
  { id: "2", name: "David Okafor", role: "Chief Officer", nationality: "Nigerian", vessel: "VOFF Crewliner 01", status: "onboard", rotationStart: "2026-03-15", rotationEnd: "2026-04-26", certStatus: "green" },
  { id: "3", name: "Marco Silva", role: "Engineer", nationality: "Brazilian", vessel: "VOFF Crewliner 01", status: "onboard", rotationStart: "2026-03-20", rotationEnd: "2026-04-30", certStatus: "amber", certIssue: "HUET expires 2026-05-10" },
  { id: "4", name: "James Chen", role: "DP Operator", nationality: "Singaporean", vessel: "VOFF Crewliner 01", status: "onboard", rotationStart: "2026-03-15", rotationEnd: "2026-04-26", certStatus: "green" },
  { id: "5", name: "André Santos", role: "Master", nationality: "Portuguese", vessel: "VOFF Combi 01", status: "onboard", rotationStart: "2026-03-10", rotationEnd: "2026-04-20", certStatus: "green" },
  { id: "6", name: "Kwame Mensah", role: "Chief Officer", nationality: "Ghanaian", vessel: "VOFF Combi 01", status: "on_leave", rotationStart: "", rotationEnd: "", certStatus: "red", certIssue: "Medical cert expired 2026-03-01", returnDate: "2026-04-18" },
  { id: "7", name: "Ivan Petrov", role: "Engineer", nationality: "Russian", vessel: "VOFF Combi 01", status: "onboard", rotationStart: "2026-03-10", rotationEnd: "2026-04-20", certStatus: "green" },
  { id: "8", name: "Carlos Mendez", role: "DP Operator", nationality: "Mexican", vessel: "VOFF Combi 01", status: "mobilising", rotationStart: "2026-04-18", rotationEnd: "2026-05-30", certStatus: "green" },
];

const VESSELS = ["VOFF Crewliner 01", "VOFF Combi 01"];

const statusBadge = (s: CrewStatus) => {
  const m: Record<CrewStatus, { label: string; cls: string }> = {
    onboard: { label: "ONBOARD", cls: "bg-[#14508C]/20 text-[#288CC8] border-[#288CC8]/30" },
    on_leave: { label: "ON LEAVE", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
    mobilising: { label: "MOBILISING", cls: "bg-[#8CB43C]/15 text-[#8CB43C] border-[#8CB43C]/30" },
    standby: { label: "STANDBY", cls: "bg-zinc-700/20 text-zinc-300 border-zinc-600/30" },
    unavailable: { label: "UNAVAILABLE", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  };
  return <Badge variant="outline" className={`text-[10px] font-bold ${m[s].cls}`}>{m[s].label}</Badge>;
};

const certDot = (s: "green" | "amber" | "red") => {
  const c = s === "green" ? "bg-emerald-400" : s === "amber" ? "bg-amber-400" : "bg-red-400";
  return <span className={`inline-block w-2 h-2 rounded-full ${c}`} />;
};

/* ── Gantt helpers ── */
const GANTT_START = new Date("2026-03-01");
const GANTT_WEEKS = 8;
const GANTT_END = new Date(GANTT_START.getTime() + GANTT_WEEKS * 7 * 86400000);
const daysBetween = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 86400000);
const totalDays = daysBetween(GANTT_START, GANTT_END);

/* ── Onboarding ── */
interface OnboardingItem { label: string; done: boolean }
const CREW_ACTIONS: OnboardingItem[] = [
  { label: "Submit STCW Basic Safety Training certificate", done: true },
  { label: "Submit Medical Fitness Certificate (ENG1)", done: true },
  { label: "Submit DP Operator Certificate", done: false },
  { label: "Submit Flag State Endorsement (RMI)", done: false },
  { label: "Submit HUET certificate", done: true },
  { label: "Submit Yellow Fever vaccination record", done: true },
  { label: "Complete VOFF SMS induction quiz", done: false },
  { label: "Sign Seafarers Employment Agreement (SEA)", done: false },
  { label: "Submit bank details for payroll", done: true },
  { label: "Sign D&A Policy acknowledgement", done: false },
];
const VOFF_ACTIONS: OnboardingItem[] = [
  { label: "Verify all certificates against originals", done: false },
  { label: "Confirm RMI endorsement valid for vessel flag", done: false },
  { label: "Issue VOFF crew ID card", done: false },
  { label: "Add crew to vessel crew list (COL)", done: true },
  { label: "Notify Flag State (RMI) of crew joining", done: false },
  { label: "Confirm travel & logistics to vessel", done: true },
  { label: "Brief on charter party client requirements", done: false },
  { label: "Add to payroll system", done: true },
  { label: "Complete vessel familiarisation record", done: false },
  { label: "Update POB list", done: true },
];

const CrewScheduler = () => {
  const [tab, setTab] = useState("rotation");
  const [vesselFilter, setVesselFilter] = useState("all");

  const filteredCrew = vesselFilter === "all" ? CREW : CREW.filter(c => c.vessel === vesselFilter);

  return (
    <IntranetLayout>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#00283C] to-[#14508C] rounded-xl p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-[#8CB43C] text-xl font-bold tracking-wider uppercase font-display">Crew Scheduler</h1>
            <p className="text-white/60 text-sm font-body mt-1">Rotation Planning, Manning Status & Onboarding</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {[{ label: "Today", value: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) }, { label: "Next Rotation", value: "12 days" }, { label: "Changes This Week", value: "2" }].map(s => (
              <div key={s.label} className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-center">
                <p className="text-white text-sm font-bold font-display">{s.value}</p>
                <p className="text-white/50 text-[10px] uppercase tracking-wider font-body">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="font-body mb-6">
          <TabsTrigger value="rotation"><Calendar className="w-4 h-4 mr-1.5" /> Rotation Board</TabsTrigger>
          <TabsTrigger value="manning"><Ship className="w-4 h-4 mr-1.5" /> Vessel Manning</TabsTrigger>
          <TabsTrigger value="database"><Users className="w-4 h-4 mr-1.5" /> Crew Database</TabsTrigger>
          <TabsTrigger value="onboarding"><BookOpen className="w-4 h-4 mr-1.5" /> Onboarding</TabsTrigger>
        </TabsList>

        {/* TAB 1: Rotation Board */}
        <TabsContent value="rotation">
          <div className="flex items-center gap-3 mb-4">
            <Select value={vesselFilter} onValueChange={setVesselFilter}>
              <SelectTrigger className="w-[200px] font-body text-sm"><SelectValue placeholder="All Vessels" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vessels</SelectItem>
                {VESSELS.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-2 text-[10px] font-body text-muted-foreground ml-auto">
              <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-[#14508C]" /> Onboard</span>
              <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-zinc-500" /> Leave</span>
              <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-[#8CB43C]" /> Mobilising</span>
              <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-red-500" /> Cert Conflict</span>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl overflow-hidden">
            {/* Timeline header */}
            <div className="flex border-b border-border">
              <div className="w-[200px] shrink-0 px-4 py-2 text-xs text-muted-foreground font-body font-bold">Crew Member</div>
              <div className="flex-1 flex">
                {Array.from({ length: GANTT_WEEKS }, (_, i) => {
                  const d = new Date(GANTT_START.getTime() + i * 7 * 86400000);
                  return <div key={i} className="flex-1 text-center text-[10px] text-muted-foreground font-body py-2 border-l border-border">{d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</div>;
                })}
              </div>
            </div>

            {VESSELS.filter(v => vesselFilter === "all" || v === vesselFilter).map(vessel => {
              const vCrew = CREW.filter(c => c.vessel === vessel);
              const onboardCount = vCrew.filter(c => c.status === "onboard").length;
              return (
                <div key={vessel}>
                  <div className="flex items-center px-4 py-2 bg-muted/40 border-b border-border">
                    <Ship className="w-3.5 h-3.5 text-[#288CC8] mr-2" />
                    <span className="text-xs font-bold text-foreground font-body">{vessel}</span>
                    <span className="text-xs text-muted-foreground font-body ml-2">— {onboardCount}/{vCrew.length} crew onboard</span>
                  </div>
                  {vCrew.map(crew => {
                    const start = crew.rotationStart ? new Date(crew.rotationStart) : null;
                    const end = crew.rotationEnd ? new Date(crew.rotationEnd) : null;
                    let barLeft = 0, barWidth = 0;
                    if (start && end) {
                      barLeft = Math.max(0, daysBetween(GANTT_START, start) / totalDays * 100);
                      barWidth = Math.min(100 - barLeft, daysBetween(start, end) / totalDays * 100);
                    }
                    return (
                      <div key={crew.id} className="flex items-center border-b border-border hover:bg-muted/20">
                        <div className="w-[200px] shrink-0 px-4 py-2.5 flex items-center gap-2">
                          {certDot(crew.certStatus)}
                          <div>
                            <p className="text-xs font-medium text-foreground font-body">{crew.name}</p>
                            <p className="text-[10px] text-muted-foreground font-body">{crew.role}</p>
                          </div>
                        </div>
                        <div className="flex-1 relative h-8">
                          {barWidth > 0 && (
                            <div
                              className={`absolute top-1 h-6 rounded ${crew.certStatus === "red" ? "bg-red-500/60 border border-red-500" : crew.status === "mobilising" ? "bg-[#8CB43C]/60 border border-[#8CB43C]" : "bg-[#14508C]/60 border border-[#288CC8]/50"}`}
                              style={{ left: `${barLeft}%`, width: `${barWidth}%` }}
                              title={`${crew.rotationStart} → ${crew.rotationEnd}`}
                            >
                              {crew.certIssue && (
                                <div className="absolute -top-1 -right-1">
                                  <AlertTriangle className="w-3 h-3 text-red-400" />
                                </div>
                              )}
                            </div>
                          )}
                          {crew.status === "on_leave" && (
                            <div className="absolute top-1 left-0 h-6 w-full bg-zinc-500/10 border border-dashed border-zinc-500/30 rounded flex items-center justify-center">
                              <span className="text-[9px] text-zinc-400 font-body">ON LEAVE — Returns {crew.returnDate}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {/* Gap warning */}
            <div className="px-4 py-3 bg-red-500/5 border-t border-red-500/20 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
              <p className="text-xs text-red-400 font-body">Chief Officer position on VOFF Combi 01 uncovered — K. Mensah on leave, medical cert expired. Action required.</p>
            </div>
          </div>
        </TabsContent>

        {/* TAB 2: Vessel Manning */}
        <TabsContent value="manning">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {VESSELS.map(vessel => {
              const vCrew = CREW.filter(c => c.vessel === vessel);
              const onboard = vCrew.filter(c => c.status === "onboard").length;
              const pct = Math.round((onboard / vCrew.length) * 100);
              const certAlerts = vCrew.filter(c => c.certStatus !== "green").length;
              return (
                <div key={vessel} className="bg-card border border-border rounded-xl overflow-hidden">
                  <div className="bg-[#00283C] p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-white text-sm font-bold font-display">{vessel.toUpperCase()}</h3>
                      <Badge variant="outline" className="text-emerald-400 border-emerald-500/30 bg-emerald-500/10 text-[10px]">● OPERATIONAL</Badge>
                    </div>
                    <p className="text-white/50 text-xs font-body mt-1">Stabroek Block, Guyana</p>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground font-body">Manning: {onboard}/{vCrew.length}</span>
                      <Progress value={pct} className="flex-1 h-2" />
                      <span className={`text-xs font-bold font-body ${pct === 100 ? "text-emerald-400" : pct >= 75 ? "text-amber-400" : "text-red-400"}`}>{pct}%</span>
                    </div>
                    <div className="space-y-2">
                      {vCrew.map(c => (
                        <div key={c.id} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                          <div className="flex items-center gap-2">
                            {certDot(c.certStatus)}
                            <div>
                              <p className="text-sm font-medium text-foreground font-body">{c.name}</p>
                              <p className="text-[10px] text-muted-foreground font-body">{c.role}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {statusBadge(c.status)}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground font-body pt-2 border-t border-border">
                      <span>Next rotation change: 12 days</span>
                      {certAlerts > 0 && <span className="text-amber-400">Cert alerts: {certAlerts} ⚠</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* TAB 3: Crew Database */}
        <TabsContent value="database">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-foreground font-body">Crew Directory</h3>
            <Button onClick={() => toast.success("Created: Add Crew", { description: "New entry added." })} size="sm" className="bg-[#8CB43C] hover:bg-[#8CB43C]/80 font-body text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Add Crew</Button>
          </div>
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-body">
                <thead>
                  <tr className="bg-muted/50 text-muted-foreground text-[11px] uppercase tracking-wider">
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Role</th>
                    <th className="text-left px-4 py-3">Nationality</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Vessel</th>
                    <th className="text-left px-4 py-3">Rotation End</th>
                    <th className="text-left px-4 py-3">Certs</th>
                  </tr>
                </thead>
                <tbody>
                  {CREW.map(c => (
                    <tr key={c.id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-3 font-medium text-foreground">{c.name}</td>
                      <td className="px-4 py-3 text-muted-foreground">{c.role}</td>
                      <td className="px-4 py-3 text-muted-foreground">{c.nationality}</td>
                      <td className="px-4 py-3">{statusBadge(c.status)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{c.vessel}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{c.rotationEnd || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {certDot(c.certStatus)}
                          {c.certIssue && <span className="text-[10px] text-amber-400">{c.certIssue}</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* TAB 4: Onboarding */}
        <TabsContent value="onboarding">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {[{ title: "Joining Soon", items: [{ name: "Carlos Mendez", role: "DP Operator", vessel: "VOFF Combi 01", joinDate: "2026-04-18", progress: 60 }], color: "text-amber-400" },
              { title: "In Progress", items: [{ name: "Kwame Mensah", role: "Chief Officer", vessel: "VOFF Combi 01", joinDate: "2026-04-25", progress: 35 }], color: "text-[#288CC8]" },
              { title: "Completed", items: [{ name: "Capt. John Smith", role: "Master", vessel: "VOFF Crewliner 01", joinDate: "2026-03-15", progress: 100 }], color: "text-emerald-400" },
            ].map(col => (
              <div key={col.title} className="bg-card border border-border rounded-xl p-4">
                <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 font-body ${col.color}`}>{col.title}</h3>
                {col.items.map((item, i) => (
                  <div key={i} className="border border-border rounded-lg p-3 mb-2">
                    <p className="text-sm font-medium text-foreground font-body">{item.name}</p>
                    <p className="text-[10px] text-muted-foreground font-body">{item.role} · {item.vessel}</p>
                    <p className="text-[10px] text-muted-foreground font-body mt-1">Join: {item.joinDate}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Progress value={item.progress} className="flex-1 h-1.5" />
                      <span className="text-[10px] font-body text-muted-foreground">{item.progress}%</span>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Checklist */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-sm font-bold text-foreground font-body mb-1">Onboarding Checklist — Carlos Mendez</h3>
            <p className="text-xs text-muted-foreground font-body mb-4">DP Operator · VOFF Combi 01 · Joining 18 Apr 2026</p>
            <div className="flex items-center gap-3 mb-6">
              <Progress value={60} className="flex-1 h-3" />
              <span className="text-sm font-bold font-body text-foreground">60% complete — 8 items outstanding</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 font-body">Crew Actions</h4>
                {CREW_ACTIONS.map((a, i) => (
                  <div key={i} className="flex items-start gap-2 py-1.5">
                    <CheckCircle className={`w-4 h-4 mt-0.5 shrink-0 ${a.done ? "text-emerald-400" : "text-muted-foreground/30"}`} />
                    <span className={`text-xs font-body ${a.done ? "text-muted-foreground line-through" : "text-foreground"}`}>{a.label}</span>
                  </div>
                ))}
              </div>
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 font-body">VOFF Actions</h4>
                {VOFF_ACTIONS.map((a, i) => (
                  <div key={i} className="flex items-start gap-2 py-1.5">
                    <CheckCircle className={`w-4 h-4 mt-0.5 shrink-0 ${a.done ? "text-emerald-400" : "text-muted-foreground/30"}`} />
                    <span className={`text-xs font-body ${a.done ? "text-muted-foreground line-through" : "text-foreground"}`}>{a.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </IntranetLayout>
  );
};

export default CrewScheduler;
